/**
 * BlackoutDatesList Component
 *
 * Displays and manages the list of blackout dates with filtering capabilities.
 * Supports franchise-specific and company-wide blackout dates.
 *
 * Features:
 * - Time range filtering (Upcoming | Past | All)
 * - Franchise filtering (All | Hamilton | North York | Company-wide)
 * - Sortable table with date ranges, franchises, and reasons
 * - Edit and delete actions with confirmation
 *
 * @see /src/features/scheduler/hooks/useBlackoutDates.ts
 * @see /src/features/scheduler/utils/schedulerFormatters.ts
 */

import React, { useState } from 'react'
import { Edit, Trash2, Calendar, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/components/ui/use-toast'
import { useBlackoutDates, useDeleteBlackoutDate } from '../hooks/useBlackoutDates'
import { formatDateRange, getFranchiseLabel, formatDuration } from '../utils/schedulerFormatters'
import type { BlackoutDate, FranchiseRegion } from '../types'
import { BlackoutDateEditor } from './BlackoutDateEditor'

type TimeRangeFilter = 'upcoming' | 'past' | 'all'
type FranchiseFilter = 'all' | 'hamilton' | 'northyork' | 'global'

interface BlackoutDatesListProps {
  className?: string
}

export function BlackoutDatesList({ className }: BlackoutDatesListProps) {
  const { toast } = useToast()
  const [timeRange, setTimeRange] = useState<TimeRangeFilter>('upcoming')
  const [franchiseFilter, setFranchiseFilter] = useState<FranchiseFilter>('all')
  const [editingBlackout, setEditingBlackout] = useState<BlackoutDate | null>(null)
  const [isEditorOpen, setIsEditorOpen] = useState(false)

  // Calculate date filters based on time range
  const today = new Date().toISOString().split('T')[0]
  const queryOptions = React.useMemo(() => {
    const options: {
      date_from?: string
      date_to?: string
      franchise_region?: FranchiseRegion | null
    } = {}

    // Time range filter
    if (timeRange === 'upcoming') {
      options.date_from = today
    } else if (timeRange === 'past') {
      options.date_to = today
    }

    // Franchise filter
    if (franchiseFilter === 'global') {
      options.franchise_region = null
    } else if (franchiseFilter !== 'all') {
      options.franchise_region = franchiseFilter as FranchiseRegion
    }

    return options
  }, [timeRange, franchiseFilter, today])

  const { data, isLoading, error } = useBlackoutDates(queryOptions)
  const deleteMutation = useDeleteBlackoutDate()

  // Filter out past dates for 'upcoming' view
  const filteredDates = React.useMemo(() => {
    if (!data?.dates) return []

    let filtered = [...data.dates]

    if (timeRange === 'past') {
      // Show only dates that have completely passed
      filtered = filtered.filter((date) => new Date(date.end_date) < new Date(today))
    }

    return filtered
  }, [data?.dates, timeRange, today])

  const handleEdit = (blackout: BlackoutDate) => {
    setEditingBlackout(blackout)
    setIsEditorOpen(true)
  }

  const handleDelete = (id: string, label: string) => {
    if (
      confirm(
        `Are you sure you want to delete the blackout date "${label}"? This action cannot be undone.`
      )
    ) {
      deleteMutation.mutate(id, {
        onSuccess: () => {
          toast({
            title: 'Blackout date deleted',
            description: `"${label}" has been removed.`,
          })
        },
        onError: (error) => {
          toast({
            title: 'Error deleting blackout date',
            description: error.message,
            variant: 'destructive',
          })
        },
      })
    }
  }

  const handleEditorClose = () => {
    setIsEditorOpen(false)
    setEditingBlackout(null)
  }

  const handleEditorSuccess = () => {
    handleEditorClose()
    toast({
      title: editingBlackout ? 'Blackout date updated' : 'Blackout date created',
      description: editingBlackout
        ? 'The blackout date has been updated successfully.'
        : 'The new blackout date has been created.',
    })
  }

  const getAppliesTo = (blackout: BlackoutDate): string => {
    if (!blackout.franchise_region) {
      return 'All Franchises'
    }
    return getFranchiseLabel(blackout.franchise_region)
  }

  const isActive = (blackout: BlackoutDate): boolean => {
    if (!blackout.is_active) return false
    const now = new Date()
    const start = new Date(blackout.start_date)
    const end = new Date(blackout.end_date)
    return now >= start && now <= end
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <p>Error loading blackout dates: {error.message}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className={className}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Blackout Dates</CardTitle>
              <CardDescription>
                Manage dates when scheduling is unavailable for franchises or service areas.
              </CardDescription>
            </div>
            <Button onClick={() => setIsEditorOpen(true)}>
              <Calendar className="mr-2 h-4 w-4" />
              Add Blackout Date
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Time Range:</label>
              <Select value={timeRange} onValueChange={(v) => setTimeRange(v as TimeRangeFilter)}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="past">Past</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Franchise:</label>
              <Select
                value={franchiseFilter}
                onValueChange={(v) => setFranchiseFilter(v as FranchiseFilter)}
              >
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="hamilton">Hamilton</SelectItem>
                  <SelectItem value="northyork">North York</SelectItem>
                  <SelectItem value="global">Company-wide</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="ml-auto text-sm text-muted-foreground">
              {filteredDates.length} blackout{filteredDates.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">Loading blackout dates...</div>
          ) : filteredDates.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No blackout dates found for the selected filters.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>Date Range</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Applies To</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDates.map((blackout) => (
                  <TableRow key={blackout.id}>
                    <TableCell className="font-medium">{blackout.label}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDateRange(blackout.start_date, blackout.end_date)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDuration(blackout.start_date, blackout.end_date)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getAppliesTo(blackout)}
                        {!blackout.franchise_region && (
                          <Badge variant="secondary" className="text-xs">
                            Global
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="line-clamp-2 text-sm text-muted-foreground">
                        {blackout.reason || '—'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {isActive(blackout) ? (
                        <Badge variant="destructive">Active Now</Badge>
                      ) : blackout.is_active ? (
                        <Badge variant="outline" className="text-success border-success">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">
                          Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEdit(blackout)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDelete(blackout.id, blackout.label)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Editor Dialog */}
      <BlackoutDateEditor
        blackoutDate={editingBlackout || undefined}
        isOpen={isEditorOpen}
        onClose={handleEditorClose}
        onSuccess={handleEditorSuccess}
      />
    </div>
  )
}
